import React, { useState } from 'react';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import CloseIcon from '../../../assets/CloseIcon.svg';

const AsyncSelectCommon = ({
  inputRef,
  loadOptions,
  value,
  name,
  isClearable = false,
  getOptionValue,
  getOptionLabel,
  placeholder,
  defaultOptions = true,
  onChange,
  onItemSelection = () => {},
  isMulti = false,
  isOptional,
  disabled = false,
  keyName = '',
  controlStyle,
  optionStyles,
  onFocus = () => {},
  onBlur = () => {},
  defaultMenuIsOpen = false,
  isOptionDisabled = () => {},
  singleValueStyle = {},
  menuListStyle = {},
  menuStyle = {},
  tabIndex,
  controlLabel,
  multiValueControlLabel,
  noOptionsMessage = undefined,
  formatOptionLabel = undefined,
  showDropdownIndicator,
  menuPlacement = 'bottom',
  // The props below intentionally have no defaults: when a consumer doesn't
  // pass them they stay undefined and react-select falls back to its own
  // native defaults, so existing usages are completely unaffected.
  menuPortalTarget,
  menuPosition,
  maxMenuHeight,
  closeMenuOnScroll,
  menuPortalStyle = {}
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#EDEEF0' : 'transparent',
      borderRadius: '8px',
      boxShadow: 'none', // This line disable the blue border
      fontSize: '14px',
      ...(!showDropdownIndicator && { padding: '4px 0px' }),
      // REMOVE ME
      // border: `1px solid yellow`,
      padding: '4px 0px',
      '&:hover': { backgroundColor: '#F7F7F7' },
      ...(state?.isDisabled && { backgroundColor: '#fcfdfd' }),
      ...controlStyle
    }),

    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),

    menu: (provided) => ({
      ...provided,
      ...menuStyle
      // boxShadow: 'none'
    }),

    menuPortal: (base) => ({
      ...base,
      ...menuPortalStyle
    }),

    menuList: (base) => ({
      ...base,
      '::-webkit-scrollbar': {
        width: '8px',
        height: '0px'
      },
      '::-webkit-scrollbar-track': {
        background: '#f1f1f1'
      },
      '::-webkit-scrollbar-thumb': {
        background: '#888'
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#555'
      },
      ...menuListStyle
    }),

    option: (provided, state) => ({
      ...provided,
      color: 'black',
      fontSize: '14px',
      padding: '6px 12px 6px 12px',
      backgroundColor: state.isSelected
        ? '#F7F7F7'
        : state.isFocused
        ? '#ebf1ff'
        : 'inherit',
      '&:hover': { backgroundColor: '#F7F7F7' },
      ...optionStyles
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: 'white',
      border: '1px solid #EDEEF0',
      borderRadius: '8px',
      fontSize: '16.6px',
      maxWidth: '100%'
    }),
    multiValueRemove: (base) => ({
      ...base,
      '&:hover': { backgroundColor: '#F7F7F7', color: 'red' }
    }),
    singleValue: (base) => ({
      ...base,
      ...singleValueStyle
    })
  };

  const customComponents = {
    DropdownIndicator: showDropdownIndicator
      ? (props) => <components.DropdownIndicator {...props} />
      : null,
    IndicatorSeparator: () => null,
    MultiValueLabel: (props) => {
      return multiValueControlLabel ? (
        multiValueControlLabel(props)
      ) : (
        <div
          className='min-w-0 overflow-hidden'
          onMouseDown={(e) => handlemultivalueclick(e, props)}
        >
          <components.MultiValueLabel {...props} />
        </div>
      );
    },
    MultiValueRemove: (props) => {
      const { data } = props;
      return disabled || data?.fixed ? (
        <div />
      ) : (
        <components.MultiValueRemove {...props}>
          <img
            src={CloseIcon}
            className='min-w-[10px] min-h-[10px] mr-1 cursor-pointer'
            alt='Close'
          />
        </components.MultiValueRemove>
      );
    },

    ClearIndicator: (props) => {
      const {
        innerProps: { ref, ...restInnerProps }
      } = props;

      return (
        <div
          {...restInnerProps}
          ref={ref}
          // style={getStyles('clearIndicator', props)}
        >
          <img
            src={CloseIcon}
            className='w-[10px] h-[10px] mr-1 cursor-pointer'
            alt='Close'
          />
        </div>
      );
    },

    ...(controlLabel && {
      SingleValue: (props) => {
        return controlLabel(props);
      }
    })
  };

  const handlemultivalueclick = (e, props) => {
    e?.stopPropagation();
    e?.preventDefault();

    onItemSelection(props);
  };

  return (
    <div className='flex flex-row items-center'>
      <div className='w-full'>
        <AsyncSelect
          tabIndex={tabIndex}
          ref={inputRef}
          key={keyName}
          cacheOptions={false}
          name={name}
          value={value}
          defaultOptions={defaultOptions}
          isClearable={isClearable}
          loadOptions={loadOptions}
          isMulti={isMulti}
          onChange={onChange}
          components={customComponents}
          styles={customStyles}
          placeholder={placeholder}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          formatOptionLabel={formatOptionLabel}
          onFocus={() => {
            setIsFocused(true);
            onFocus();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur();
          }}
          isDisabled={disabled}
          defaultMenuIsOpen={defaultMenuIsOpen}
          isOptionDisabled={isOptionDisabled}
          noOptionsMessage={noOptionsMessage}
          menuPlacement={menuPlacement}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          maxMenuHeight={maxMenuHeight}
          closeMenuOnScroll={closeMenuOnScroll}
        />
      </div>
      {isFocused && isOptional && (
        <span className='text-xs font-medium text-neutral-500 right-2 ml-2.5'>
          Optional
        </span>
      )}
    </div>
  );
};

export default AsyncSelectCommon;
